/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { Component } from 'react';
import propTypes from 'prop-types';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import partition from 'lodash/partition';

import HoverableListItem from 'app/HoverableListItem/components/HoverableListItem';
import ContextMenu from 'app/ContextMenu/components/ContextMenu';
import { IconButton } from 'ui/Components';
import { compassLink, live, scheduled } from 'ui/Icons';
import { makeId } from 'utils/selectors';

import styles from './FolderAndPageList.css';

/**
 * Renders list of Folders and Pages
 */
export default class FolderAndPageList extends Component {
  static propTypes = {
    currentPageId: propTypes.string,
    foldersAndPages: propTypes.arrayOf(
      propTypes.shape({
        Type: propTypes.string.isRequired,
        Id: propTypes.string.isRequired,
        Name: propTypes.string.isRequired
      })
    ),
    isOnRealRoot: propTypes.bool,
    itemActions: propTypes.arrayOf(
      propTypes.shape({
        text: propTypes.string,
        action: propTypes.string,
        disabled: propTypes.bool
      })
    ),
    onActionSelect: propTypes.func,
    onItemSelect: propTypes.func,
    setActions: propTypes.func
  };

  static defaultProps = {
    currentPageId: undefined,
    foldersAndPages: undefined,
    isOnRealRoot: false,
    itemActions: undefined,
    onActionSelect: undefined,
    onItemSelect: undefined,
    setActions: null
  };

  constructor(props) {
    super(props);
    this.selected = React.createRef();
  }

  componentDidMount() {
    this.scrollToListItem();
  }

  componentDidUpdate() {
    this.scrollToListItem();
  }

  /**
   * does child object represent a page
   * @param {object} child - page or folder data
   * @returns {boolean} yes or no
   */
  isPage(child) {
    return child.Type === 'page';
  }

  /**
   * does child object represent a live page
   * @param {object} child - page data
   * @returns {boolean} yes or no
   */
  isPageLive(child) {
    return this.isPage(child) && child.Live;
  }

  /**
   * is page scheduled to be published
   * @param {object} child - page data
   * @returns {boolean} yes or no
   */
  isPageScheduled(child) {
    return this.isPage(child) && !child.Live && child.Scheduled;
  }

  /**
   * is page a compass page
   * @param {object} child - page data
   * @returns {boolean} yes or no
   */
  isCompassPage(child) {
    return this.isPage(child) && child.IsCompassPage;
  }

  /**
   * is currently selected
   * @param {object} child - page data
   * @returns {boolean} yes or no
   */
  isSelected(child) {
    const { currentPageId } = this.props;
    return child.Id === currentPageId;
  }

  /**
   * handle scrolling to selected list item
   */
  scrollToListItem() {
    if (this.selected.current) {
      this.selected.current.scrollIntoView();
    }
  }

  /**
   * should render additional actions for the user
   * @returns {boolean} yes or no
   */
  shouldRenderContextMenu() {
    const { isOnRealRoot, itemActions, setActions } = this.props;
    return !isOnRealRoot && (!isEmpty(itemActions) || setActions !== null);
  }

  /**
   * handle styling of child elements
   * @param {object} child - page data
   * @returns {string} class style
   */
  styleItem(child) {
    return classnames('d-flex align-items-center', styles.itemWrapper, {
      [styles.selected]: this.isSelected(child)
    });
  }

  /**
   * render an icon
   * @param {Node} icon - svg icon
   * @param {string} alt - image alternate text
   * @param {string} name - folder/page/component name
   * @returns {React.Component} icon
   */
  renderIcon(icon, alt, name) {
    return (
      <IconButton
        alt={alt}
        icon={icon}
        noMargin
        rem={2}
        data-test-id={makeId({ name, type: alt })}
      />
    );
  }

  /**
   * render live icon
   * @param {object} child - page data
   * @returns {Function} live page icon
   */
  renderLiveIcon(child) {
    return this.renderIcon(live, 'Live Page', child.Name);
  }

  /**
   * render schedule page icon
   * @param {object} child - page data
   * @returns {Function} scheduled page icon
   */
  renderScheduledIcon(child) {
    return this.renderIcon(scheduled, 'Scheduled Page', child.Name);
  }

  /**
   * render compass page icon
   * @param {object} child - page data
   * @returns {Function} compass page icon
   */
  renderCompassPageIcon(child) {
    return this.renderIcon(compassLink, 'Compass Page', child.Name);
  }

  render() {
    const {
      foldersAndPages,
      onActionSelect,
      onItemSelect,
      itemActions,
      setActions
    } = this.props;

    if (!foldersAndPages) {
      return <div>Loading ...</div>;
    }
    const children = orderBy(
      foldersAndPages,
      [child => child.Type.toLowerCase(), child => child.Name.toLowerCase()],
      ['asc', 'asc']
    );

    const [pages, folders] = partition(children, child => this.isPage(child));
    const [livePages, nonLivePages] = partition(pages, child => this.isPageLive(child));
    const [scheduledPages, remainingPages] = partition(nonLivePages, child =>
      this.isPageScheduled(child)
    );

    const items = [...folders, ...scheduledPages, ...livePages, ...remainingPages];

    return (
      <div className="mt-2">
        {items.map(child => (
          <div
            key={`div-${child.Id}`}
            className={this.styleItem(child)}
            data-test-id={child.Id}
            ref={this.isSelected(child) ? this.selected : undefined}
          >
            <div
              className={styles.clickWrapper}
              onClick={() => onItemSelect(child)}
              data-test-id="folderAndPageList-handle-click"
            >
              <HoverableListItem name={child.Name} type={child.Type} />
            </div>
            {this.isPageScheduled(child) && this.renderScheduledIcon(child)}
            {this.isPageLive(child) && this.renderLiveIcon(child)}
            {this.isCompassPage(child) && this.renderCompassPageIcon(child)}
            {this.shouldRenderContextMenu() && (
              <ContextMenu
                actions={itemActions || setActions(child)}
                onActionSelect={action => onActionSelect(child, action)}
                popoverPlacement="left"
                rem={1.5}
              />
            )}
          </div>
        ))}
      </div>
    );
  }
}
